import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import z from 'zod';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { deleteVintage, saveVintage } from '@apple/features/vintage/api';
import { VintageForm } from '@apple/features/vintage/components/VintageForm';
import { vintageQueries, vintageQueryKeys } from '@apple/features/vintage/queries';

export const Route = createFileRoute('/_authed/_admin/manage/vintages_/$vintageId')({
	parseParams: ({ vintageId }) => ({ vintageId: z.string().parse(vintageId) }),
	beforeLoad: ({ params: { vintageId }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		});

		return {
			vintageQueryOptions: vintageQueries.vintage(Number(vintageId)),
			vintageStoresQueryOptions: vintageQueries.stores(Number(vintageId)),
			vintageProductsQueryOptions: vintageQueries.products(Number(vintageId)),
		};
	},
	loader: ({
		context: {
			queryClient,
			vintageQueryOptions,
			vintageStoresQueryOptions,
			vintageProductsQueryOptions,
		},
	}) => {
		void queryClient.prefetchQuery(vintageQueryOptions);
		void queryClient.prefetchQuery(vintageStoresQueryOptions);
		void queryClient.prefetchQuery(vintageProductsQueryOptions);
	},
	wrapInSuspense: true,
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('vintage');
	const { vintageQueryOptions, vintageStoresQueryOptions, vintageProductsQueryOptions } =
		Route.useRouteContext();
	const vintage = useSuspenseQuery(vintageQueryOptions);
	const stores = useSuspenseQuery(vintageStoresQueryOptions);
	const products = useSuspenseQuery(vintageProductsQueryOptions);
	const queryClient = useQueryClient();
	const goBackToList = useBackOrNavigate();

	const saveVintageMutation = useMutation({
		mutationFn: saveVintage,
		onSuccess: async () => {
			notifications.show({ color: 'green', message: t('save.success'), icon: <Success /> });
			await queryClient.invalidateQueries({ queryKey: vintageQueryKeys.all });
			await goBackToList('/manage/vintages');
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	const deleteVintageMutation = useMutation({
		mutationFn: deleteVintage,
		onSuccess: async () => {
			notifications.show({ color: 'green', message: t('delete.success'), icon: <Success /> });
			await queryClient.invalidateQueries({ queryKey: vintageQueryKeys.all });
			await goBackToList('/manage/vintages');
		},
		onError: () => {
			notifications.show({
				color: 'red',
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
			});
		},
	});

	const handleDelete = useCallback(
		(vintageId: number) => {
			modals.openConfirmModal({
				title: t('delete.title'),
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				confirmProps: { color: 'red' },
				onConfirm: () => deleteVintageMutation.mutate(vintageId),
				children: <Text>{t('delete.message')}</Text>,
			});
		},
		[deleteVintageMutation, t],
	);

	return (
		<DetailsLayout title={t('titles.edit')}>
			{vintage.data && (
				<VintageForm
					vintage={vintage.data}
					initialStores={stores.data}
					initialProducts={products.data}
					onSave={async values => {
						await saveVintageMutation.mutateAsync(values);
					}}
					onDelete={handleDelete}
					isSaving={saveVintageMutation.isPending || saveVintageMutation.isSuccess}
				/>
			)}
		</DetailsLayout>
	);
}
